import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea
} from "@adora/shadcn-ui";
import { useToast } from "@adora/shared/hooks/use-toast";
import { useAdjustTokens } from "../../hooks/use-token-admin";
import type { WalletType } from "../../types/token-wallet";

type BalanceVariant = "balance_topup" | "balance_deduction";

interface AdjustBalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number;
  walletType: WalletType;
  variant: BalanceVariant;
}

const schema = z.object({
  amount: z.number().int().positive("Enter a positive amount"),
  reason: z.string().trim().min(1, "Reason is required").max(500)
});

const COPY: Record<BalanceVariant, { title: string; description: string; cta: string }> = {
  balance_topup: {
    title: "Top Up Balance",
    description: "Add tokens to this brand's wallet.",
    cta: "Top Up"
  },
  balance_deduction: {
    title: "Deduct Balance",
    description: "Remove tokens from this brand's wallet.",
    cta: "Deduct"
  }
};

export default function AdjustBalanceDialog({
  open,
  onOpenChange,
  brandId,
  walletType,
  variant
}: AdjustBalanceDialogProps) {
  const { openToast } = useToast();
  const { mutate, isPending } = useAdjustTokens(brandId);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Generated once per dialog-open and reused across retries of this submit so a
  // retried request is applied at most once server-side.
  const [idempotencyKey, setIdempotencyKey] = useState("");

  const copy = COPY[variant];

  useEffect(() => {
    if (open) {
      setAmount("");
      setReason("");
      setErrors({});
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open]);

  const handleSubmit = () => {
    const parsed = schema.safeParse({ amount: Number(amount), reason });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as string] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    mutate(
      {
        brand_id: brandId,
        adjustment_type: variant,
        amount: parsed.data.amount,
        reason: parsed.data.reason,
        idempotency_key: idempotencyKey,
        wallet_type: walletType
      },
      {
        onSuccess: () => {
          openToast(`${copy.cta} successful`);
          onOpenChange(false);
        },
        onError: (error: Error) => openToast(error.message || "Adjustment failed")
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="amount">Amount</Label>
            <Input id="amount" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
            {errors.amount && <p className="text-sm text-destructive">{errors.amount}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={reason}
              placeholder="Recorded in the audit ledger"
              onChange={(e) => setReason(e.target.value)}
            />
            {errors.reason && <p className="text-sm text-destructive">{errors.reason}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Saving…" : copy.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
